import { type ReactNode } from "react";

export interface HeroCardSegment {
  /** 0–100 completion percentage */
  progress: number;
}

export interface HeroCardAction {
  label: string;
  icon?: ReactNode;
}

/**
 * `hero` est la carte pleine de `/learn`, où le programme est le sujet de la
 * page. `compact` est la même carte intercalée dans un écran qui a déjà ses
 * titres : à 30 px et 700 de graisse, elle devenait le texte le plus lourd de
 * la bibliothèque, plus lourd que son propre `h1` qui est en 300.
 */
export type HeroCardDensity = "hero" | "compact";

const DENSITIES: Record<
  HeroCardDensity,
  {
    padding: string;
    title: string;
    description: string;
    orb: string;
  }
> = {
  hero: {
    padding: "p-10",
    title: "text-3xl font-bold",
    description: "text-[15px] mb-6",
    orb: "size-80 blur-[60px] opacity-15 group-hover:opacity-25",
  },
  compact: {
    padding: "p-6",
    title: "text-[20px] font-semibold",
    description: "text-[14px] mb-4",
    orb: "size-56 blur-[48px] opacity-10 group-hover:opacity-20",
  },
};

export interface HeroCardProps {
  /** HSL hue (0–360) for the gradient background */
  hue: number;
  /** Hex accent color for highlights, progress, action */
  color: string;
  title: string;
  badge?: string;
  description?: string;
  /** Progress bar segments */
  segments?: HeroCardSegment[];
  /** Metadata strings shown next to the progress bar */
  meta?: string[];
  /** CTA rendered at the bottom */
  action?: HeroCardAction;
  onClick?: () => void;
  /** Défaut `hero`. Voir {@see HeroCardDensity}. */
  density?: HeroCardDensity;
}

export function HeroCard({
  hue,
  color,
  title,
  badge,
  description,
  segments,
  meta,
  action,
  onClick,
  density = "hero",
}: HeroCardProps) {
  const Tag = onClick ? "button" : "div";
  const scale = DENSITIES[density];

  return (
    <Tag
      onClick={onClick}
      className="group relative w-full overflow-hidden rounded-2xl text-left
        transition-all duration-300 hover:scale-[1.008] active:scale-[0.998]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{
        background: `linear-gradient(135deg, hsla(${hue}, 35%, 13%, 0.8) 0%, hsla(${hue}, 18%, 8%, 0.8) 100%)`,
        border: "1px solid var(--color-border-subtle)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
        backdropFilter: "blur(8px) saturate(1.2)",
        WebkitBackdropFilter: "blur(8px) saturate(1.2)",
      }}
    >
      {/* Ambient glow orb */}
      <div
        className={`absolute -top-24 -right-24 rounded-full transition-opacity duration-500 pointer-events-none ${scale.orb}`}
        style={{ background: `radial-gradient(circle, ${color}, transparent 70%)` }}
      />

      {/* Content */}
      <div className={`relative z-10 flex flex-col justify-center max-w-xl ${scale.padding}`}>
        {badge && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] mb-3 opacity-70" style={{ color }}>
            {badge}
          </span>
        )}

        <h2 className={`tracking-tight text-text-primary mb-2 leading-tight ${scale.title}`}>{title}</h2>

        {description && (
          <p className={`text-text-secondary leading-relaxed max-w-md ${scale.description}`}>{description}</p>
        )}

        {/* Progress segments + meta */}
        {((segments?.length ?? 0) > 0 || (meta?.length ?? 0) > 0) && (
          <div className="flex items-center gap-5">
            {segments && segments.length > 0 && (
              <div className="flex gap-px">
                {segments.map((seg, i) => (
                  <div
                    key={i}
                    className="h-1 w-9 rounded-full transition-all duration-500"
                    style={{
                      background:
                        seg.progress >= 100
                          ? color
                          : seg.progress > 0
                            ? `color-mix(in srgb, ${color} 50%, transparent)`
                            : "rgba(255,255,255,0.08)",
                    }}
                  />
                ))}
              </div>
            )}

            {meta?.map((text, i) => (
              <span key={i} className="text-xs text-text-muted">
                {text}
              </span>
            ))}
          </div>
        )}

        {/* Action button */}
        {action && (
          <div className="mt-5">
            <span
              className="inline-flex items-center gap-2 h-9 px-5 rounded-lg text-xs font-medium transition-all group-hover:brightness-110"
              style={{
                background: `color-mix(in srgb, ${color} 10%, transparent)`,
                color,
                border: `1px solid color-mix(in srgb, ${color} 16%, transparent)`,
              }}
            >
              {action.icon}
              {action.label}
            </span>
          </div>
        )}
      </div>
    </Tag>
  );
}
